import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'La reprogrammation moteur est-elle légale en France ?',
      answer: 'Oui, la reprogrammation moteur est légale en France tant qu\'elle respecte certaines normes. Nos modifications sont compatibles avec le contrôle technique et nous conservons toutes les sécurités du véhicule. Nous proposons également un retour à l\'origine gratuit à tout moment.'
    },
    {
      question: 'Est-ce que ma garantie constructeur sera annulée ?',
      answer: 'La reprogrammation peut potentiellement affecter votre garantie constructeur si celle-ci est toujours active. Cependant, nous proposons un retour à l\'origine offert avant toute intervention en concession. De plus, nos fichiers sont optimisés pour préserver la fiabilité du moteur.'
    },
    {
      question: 'Quels sont les gains de performance attendus ?',
      answer: 'Les gains varient selon le véhicule et la motorisation. En moyenne, attendez-vous à +25-35% de puissance et +30-40% de couple sur les moteurs diesel, et +15-25% de puissance et +20-30% de couple sur les moteurs essence. Utilisez notre configurateur pour une estimation personnalisée.'
    },
    {
      question: 'Combien de temps dure l\'intervention ?',
      answer: 'Une reprogrammation moteur prend généralement entre 2 et 3 heures, incluant la lecture de l\'ECU d\'origine, la programmation du nouveau fichier optimisé et le test sur route pour vérifier les performances et le bon fonctionnement.'
    },
    {
      question: 'Est-ce que cela augmente la consommation de carburant ?',
      answer: 'Au contraire ! Une reprogrammation bien réalisée peut réduire votre consommation de 0,5 à 1,5 L/100km en conduite normale. Le moteur étant plus performant, il n\'a plus besoin de "forcer" pour atteindre les mêmes performances, ce qui optimise la consommation.'
    },
    {
      question: 'Puis-je revenir à la configuration d\'origine ?',
      answer: 'Absolument ! Nous sauvegardons systématiquement votre fichier d\'origine et proposons un retour gratuit à tout moment. La procédure de retour prend environ 1 heure et remet votre véhicule exactement dans son état initial.'
    },
    {
      question: 'Quels véhicules peuvent être reprogrammés ?',
      answer: 'Nous pouvons reprogrammer la quasi-totalité des véhicules équipés d\'un calculateur électronique, soit tous les véhicules depuis environ 2000. Cela inclut les voitures essence, diesel, hybrides, ainsi que les utilitaires et certains poids lourds. Utilisez notre configurateur pour vérifier votre modèle.'
    },
    {
      question: 'Y a-t-il des risques pour mon moteur ?',
      answer: 'Non, si la reprogrammation est réalisée par un professionnel avec des fichiers de qualité. Nous respectons les limites mécaniques de votre moteur et conservons toutes les sécurités d\'origine (température, pression, etc.). Nos fichiers sont développés et testés pour garantir la fiabilité à long terme.'
    },
    {
      question: 'Proposez-vous une garantie ?',
      answer: 'Oui, nous garantissons notre travail. En cas de problème lié à la reprogrammation, nous intervenons gratuitement pour corriger le souci ou effectuer un retour à l\'origine sans frais. Votre satisfaction est notre priorité.'
    },
    {
      question: 'Comment prendre rendez-vous ?',
      answer: 'Vous pouvez nous contacter directement par téléphone au 07 49 99 57 28 ou utiliser notre formulaire de contact sur le site. Nous sommes basés à Creutzwald (57) et proposons également des interventions à domicile dans certains cas.'
    }
  ];

  return (
    <section className="py-20 px-4 bg-slate-900">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-4 bg-red-600/10 rounded-full mb-6">
            <HelpCircle className="h-12 w-12 text-red-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Questions Fréquentes
          </h2>
          <p className="text-gray-400 text-lg">
            Tout ce que vous devez savoir sur la reprogrammation moteur
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl border-2 border-slate-700 overflow-hidden transition-all duration-300 hover:border-red-600"
              style={{
                animation: `slideIn 0.4s ease-out ${index * 0.05}s both`
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left transition-colors hover:bg-slate-700/30"
              >
                <span className="text-lg font-semibold text-white pr-8">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`h-6 w-6 text-red-600 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-6 text-gray-400 leading-relaxed border-t border-slate-700 pt-4">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center p-8 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl border-2 border-slate-700">
          <p className="text-white text-xl font-semibold mb-4">
            Vous avez d'autres questions ?
          </p>
          <p className="text-gray-400 mb-6">
            Notre équipe est là pour vous répondre
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="tel:0749995728"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition-all transform hover:scale-105"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Appelez-nous
            </a>
            <a
              href="#contact"
              className="inline-flex items-center bg-slate-700 hover:bg-slate-600 text-white font-semibold px-6 py-3 rounded-lg transition-all"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contactez-nous
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
};

export default FAQ;
